import React, { useState, useEffect, ReactNode } from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import "./styles.scss";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { LoadingButton } from "@mui/lab";
import { CloseOutlined } from "@mui/icons-material";
import { setDistributions } from "../../redux/features/partnershipsSlice";
import { useDispatch } from "react-redux";
import { Partner, PartnershipType } from "../../types/genericType";
import {
  createDistribution,
  getInvestorsByPartnershipId,
  getPartnerShipDistributions,
} from "../../services/partnershipService";
import CustomSnackbar from "../common/Snackbar/CustomSnackbar";
import { generateClientName } from "../../utils/utils";
import AmountInputField from "../common/NumericField/AmountField";

type SummaryModalProps = {
  handleClose: Function;
  partnershipId: number | undefined;
  selectedPartnership: PartnershipType | null;
};

const SummaryModal = (props: SummaryModalProps) => {
  const { handleClose, partnershipId } = props;

  const [distributionData, setDistributionData] = useState<Partner[]>([]);
  const [onSuccess, setOnSuccess] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState<Dayjs>(dayjs(Date()));
  const [description, setDescription] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [totalPayout, setTotalPayout] = useState("0");

  const dispatch = useDispatch();

  useEffect(() => {
    if (partnershipId) {
      getInvestorsByPartnershipId(partnershipId)
        .then((res) => {
          setDistributionData(res);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [partnershipId]);

  useEffect(() => {
    let total = 0;
    let updatedPartners: Partner[] = [];
    distributionData.forEach((partner) => {
      partner.payout = (parseFloat(amount) * partner.percentage) / 100;
      updatedPartners.push(partner);
      total += parseFloat(partner.payout.toFixed(2));
    });
    setDistributionData(updatedPartners);
    setTotalPayout(total.toFixed(2));
  }, [amount]);

  const handleNewDistribution = () => {
    setIsLoading(true);
    createDistribution(partnershipId, Number(amount), date, description)
      .then((res) => {
        console.log(res);

        getPartnerShipDistributions(partnershipId)
          .then((distributions) => {
            dispatch(setDistributions(distributions));
            handleClose();
          })
          .catch((err) => {
            console.log(err);
          });

        setIsLoading(false);
        setSnackbarMsg("New Distribution was Added.");
        setOnSuccess(true);
      })
      .catch((err) => {
        console.log(err);
        setOnSuccess(true);
        setSnackbarMsg("Failed to add new distribution.");
        setIsLoading(false);
      });
  };

  const handleCloseModal = () => {
    setAmount("");
    setTotalPayout("");
    const updatedPartners = distributionData.map((partner) => {
      partner.payout = 0.0;
      return partner;
    });
    setDistributionData(updatedPartners);
    handleClose();
  };

  return (
    <Box className="summary-modal">
      <Box className="new-distribution">
        <Stack spacing={3}>
          <Typography className="new-distribution-title">
            New Distribution
          </Typography>
          <AmountInputField amount={amount} setAmount={setAmount} required />
          <Box className="date-picker-container">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Post date"
                className="date-picker-container"
                slotProps={{ textField: { size: "small" } }}
                value={date}
                // @ts-ignore
                onChange={(newValue) => setDate(newValue)}
                sx={{
                  "& .Mui-selected": {
                    color: "#FFFFFF",
                    borderRadius: 2,
                    borderWidth: 1,
                    borderColor: "#e91e63",
                    border: "1px solid",
                    backgroundColor: "#f8bbd0",
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
          <TextField
            label="Description"
            multiline
            minRows={4}
            maxRows={6}
            value={description}
            onChange={(event) => setDescription(event.target.value as string)}
          />
          <Stack direction="row-reverse" spacing={2}>
            <LoadingButton
              variant="contained"
              data-testid="createTxnBtn"
              size="small"
              className={`run-button ${amount === "" ? "disabled" : ""}`}
              disabled={amount === ""}
              onClick={handleNewDistribution}
              loading={isLoading}
            >
              RUN
            </LoadingButton>
            <Button className="cancel-button" onClick={handleCloseModal}>
              CANCEL
            </Button>
          </Stack>
        </Stack>
      </Box>
      <Box className="distribution-summary">
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography
            fontSize={20}
            fontWeight={500}
            color="var(--text-secondary, rgba(0, 0, 0, 0.60))"
          >
            Distribution Summary
          </Typography>
          <IconButton onClick={handleCloseModal} size="large">
            <CloseOutlined />
          </IconButton>
        </Box>
        <TableContainer component={Paper} elevation={0}>
          <Table className="partners-table-container" aria-label="simple table">
            <caption>
              <div className="table-caption">
                <Typography className="total-label">Total</Typography>
                <Typography className="total-label">
                  {totalPayout ? `$${totalPayout}` : "--"}
                </Typography>
              </div>
            </caption>
            <TableHead>
              <TableRow>
                <TableCell className="client-header">Partner</TableCell>
                <TableCell align="left" className="account-header">
                  Account
                </TableCell>
                <TableCell align="right" className="percent-owned-header">
                  Percent Owned
                </TableCell>
                <TableCell align="right" className="payout-header">
                  Payout
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {distributionData.reduce((acc: ReactNode[], row) => {
                if (row?.account?.isActive) {
                  return [
                    ...acc,
                    <TableRow key={row.id}>
                      <TableCell
                        component="th"
                        scope="row"
                        className="row-cell"
                      >
                        {generateClientName(row.investor)}
                      </TableCell>
                      <TableCell align="left" className="row-cell">
                        {row.account.name}
                      </TableCell>
                      <TableCell align="right" className="row-cell">
                        {row.percentage} %
                      </TableCell>
                      <TableCell align="right" className="row-cell">
                        {row.payout ? `$${row.payout.toFixed(2)}` : "-"}
                      </TableCell>
                    </TableRow>,
                  ];
                }
                return acc;
              }, [])}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <CustomSnackbar
        open={onSuccess}
        message={snackbarMsg}
        setOpen={setOnSuccess}
      />
    </Box>
  );
};

export default SummaryModal;
